import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  ConfirmDialog,
  Modal,
  RadioGroup,
  Skeleton,
  TextArea,
  useToast,
} from '@bbpos/ui';
import type { Tenant } from '@/types/tenant';
import { subscriptionService } from '@/services/subscription.service';
import { PlanActionsPanel } from '@/components/features/subscriptions/PlanActionsPanel';
import { InvoiceHistoryPanel } from '@/components/features/subscriptions/InvoiceHistoryPanel';
import { ChangeHistoryTimeline } from '@/components/features/subscriptions/ChangeHistoryTimeline';
import { TenantLimitsPanel } from '@/components/features/tenants/TenantLimitsPanel';
import {
  GLASS_PANEL,
  cn,
  formatINRDate,
  planTone,
  subscriptionDaysRemaining,
  toneClasses,
} from '@/components/features/subscriptions/design-tokens';

// ─── Icons ──────────────────────────────────────────────────────────────────
function PlanIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}
function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
function CalendarStartIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}
function CalendarEndIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2zM9 16l2 2 4-4" />
    </svg>
  );
}
function PowerIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M18.36 6.64a9 9 0 11-12.73 0M12 2v10" />
    </svg>
  );
}
function HourglassIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 2h12M6 22h12M7 4v3a5 5 0 0010 0V4M7 20v-3a5 5 0 0110 0v3" />
    </svg>
  );
}
function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 3h6a1 1 0 011 1v3H8V4a1 1 0 011-1z" />
    </svg>
  );
}

import type { Tone } from '@/components/features/subscriptions/design-tokens';

function SubKpiCard({
  label,
  value,
  supporting,
  tone,
  icon,
}: {
  label: string;
  value: string | number;
  supporting?: string;
  tone: Tone;
  icon: React.ReactNode;
}) {
  const palette = toneClasses(tone);
  return (
    <div className={cn(GLASS_PANEL, 'relative overflow-hidden p-5 transition-all duration-200 hover:shadow-lg')}>
      <div className={cn('absolute inset-x-0 top-0 h-14 bg-gradient-to-b', palette.accent)} />
      <div className="relative space-y-3">
        <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl', palette.icon)}>
          {icon}
        </div>
        <div>
          <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{label}</p>
          <p className="mt-1 text-2xl font-semibold tracking-tight text-neutral-950 dark:text-neutral-50 tabular-nums">
            {value}
          </p>
        </div>
        {supporting && (
          <p className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">{supporting}</p>
        )}
      </div>
    </div>
  );
}

const EXTEND_OPTIONS = [
  { value: '7', label: '+7 days' },
  { value: '14', label: '+14 days' },
  { value: '30', label: '+30 days' },
];

interface TenantSubscriptionTabProps {
  tenant: Tenant;
  /** ADMIN+ (80+) — when false the action panel is disabled but still visible. */
  canManage: boolean;
  /** SUPER_ADMIN (100) — gates the cancel action. */
  canCancel?: boolean;
}

export function TenantSubscriptionTab({ tenant, canManage, canCancel = false }: TenantSubscriptionTabProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: details } = useQuery({
    queryKey: ['subscription', 'details', tenant.id],
    queryFn: () => subscriptionService.getDetails(tenant.id),
  });

  // Provisioning starts a trial (trial_ends_at) without a subscription period,
  // so the trial end stands in until a subscription is recorded. Extending the
  // trial stamps subscription_expires_at = trial_ends_at, which is still the trial.
  const trialRunning =
    !!tenant.trial_ends_at &&
    (!tenant.subscription_expires_at ||
      Date.parse(tenant.subscription_expires_at) === Date.parse(tenant.trial_ends_at));
  const endsAt = tenant.subscription_expires_at ?? tenant.trial_ends_at;
  const endDate = endsAt ? new Date(endsAt) : null;
  const daysRemaining = subscriptionDaysRemaining(endsAt);
  const totalDays =
    tenant.subscription_started_at && tenant.subscription_expires_at
      ? Math.ceil(
          (new Date(tenant.subscription_expires_at).getTime() -
            new Date(tenant.subscription_started_at).getTime()) /
            86400000,
        )
      : 1;
  const progressPct = totalDays > 0 ? Math.round(((totalDays - daysRemaining) / totalDays) * 100) : 0;

  const progressColor =
    daysRemaining > 30 ? 'bg-emerald-500' : daysRemaining > 7 ? 'bg-amber-500' : 'bg-rose-500';

  const isTrial =
    tenant.status === 'TRIAL' || tenant.subscription_plan === 'TRIAL' || trialRunning;
  const isSuspended = tenant.status === 'SUSPENDED' || details?.is_read_only === true;

  // ─── Action state ─────────────────────────────────────────────────────────
  const [extendOpen, setExtendOpen] = useState(false);
  const [extendDays, setExtendDays] = useState('14');
  const [extendNotes, setExtendNotes] = useState('');

  const [reactivateOpen, setReactivateOpen] = useState(false);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['subscription'] });
    queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id] });
    queryClient.invalidateQueries({ queryKey: ['tenants'] });
  }

  const extendMutation = useMutation({
    mutationFn: () =>
      subscriptionService.extendTrial(tenant.id, {
        days: Number(extendDays),
        notes: extendNotes.trim() || undefined,
      }),
    onSuccess: () => {
      toast({ title: `Trial extended by ${extendDays} days`, variant: 'success' });
      setExtendOpen(false);
      setExtendNotes('');
      invalidate();
    },
    onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
  });

  const reactivateMutation = useMutation({
    mutationFn: () => subscriptionService.reactivate(tenant.id, { duration_days: 30 }),
    onSuccess: () => {
      toast({ title: 'Tenant reactivated for 30 days', variant: 'success' });
      setReactivateOpen(false);
      invalidate();
    },
    onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
  });

  const cancelMutation = useMutation({
    mutationFn: () =>
      subscriptionService.cancel(tenant.id, { reason: cancelReason.trim() || undefined }),
    onSuccess: () => {
      toast({
        title: 'Subscription cancellation recorded',
        description: 'Pending invoices voided. The tenant status is unchanged.',
        variant: 'success',
      });
      setCancelOpen(false);
      setCancelReason('');
      invalidate();
    },
    onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
  });

  return (
    <div className="space-y-6 animate-slide-up">
      {/* Trial / grace warnings */}
      {isTrial && daysRemaining <= 14 && (
        <Alert
          variant="warning"
          title="Trial expiring soon"
          description={`This tenant's trial expires in ${daysRemaining} days. Extend the trial or upgrade to avoid service interruption.`}
          action={
            canManage
              ? {
                  label: 'Extend trial',
                  onClick: () => setExtendOpen(true),
                }
              : undefined
          }
        />
      )}
      {isSuspended && (
        <Alert
          variant="danger"
          title="Subscription expired"
          description="This tenant is in grace period with read-only access. Reactivate to restore full access."
          action={
            canManage
              ? {
                  label: 'Reactivate',
                  onClick: () => setReactivateOpen(true),
                }
              : undefined
          }
        />
      )}
      {details?.pending_downgrade && (
        <Alert
          variant="info"
          title="Downgrade scheduled"
          description={`Plan will change to ${details.pending_downgrade.new_plan} on ${formatINRDate(details.pending_downgrade.effective_date)}.`}
        />
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SubKpiCard
          label="Current Plan"
          value={
            details?.current_plan.display_name ??
            tenant.subscription_plan ??
            'None'
          }
          tone={planTone(tenant.subscription_plan)}
          icon={<PlanIcon />}
        />
        <SubKpiCard
          label="Days Remaining"
          value={endDate ? daysRemaining : '—'}
          supporting={daysRemaining <= 7 && endDate ? 'Expiring soon' : undefined}
          tone={daysRemaining > 30 ? 'success' : daysRemaining > 7 ? 'warning' : 'danger'}
          icon={<ClockIcon />}
        />
        <SubKpiCard
          label="Start Date"
          value={formatINRDate(tenant.subscription_started_at)}
          tone="info"
          icon={<CalendarStartIcon />}
        />
        <SubKpiCard
          label="End Date"
          value={formatINRDate(endsAt)}
          tone="info"
          icon={<CalendarEndIcon />}
        />
      </div>

      {/* Quick-action toolbar for platform roles */}
      {canManage && (isTrial || isSuspended) && (
        <div className={cn(GLASS_PANEL, 'flex flex-wrap items-center gap-3 p-4')}>
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 to-indigo-500 text-white shadow-[0_8px_20px_-8px_rgba(14,165,233,0.55)]">
            <HourglassIcon />
          </div>
          <div className="flex-1 min-w-[220px]">
            <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              Quick lifecycle actions
            </p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              {isSuspended
                ? 'This tenant is blocked. Reactivate to restore full access for 30 days.'
                : `Trial ends in ${daysRemaining} days. Extend it without converting the tenant to a paid plan.`}
            </p>
          </div>
          {isTrial && (
            <Button variant="secondary" onClick={() => setExtendOpen(true)}>
              <HourglassIcon />
              <span className="ml-1.5">Extend trial</span>
            </Button>
          )}
          {isSuspended && (
            <Button variant="primary" onClick={() => setReactivateOpen(true)}>
              <PowerIcon />
              <span className="ml-1.5">Reactivate</span>
            </Button>
          )}
        </div>
      )}

      {/* Subscription progress */}
      {endDate && (
        <div className={cn(GLASS_PANEL, 'p-6 transition-shadow duration-200 hover:shadow-lg')}>
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500 mb-4">
            Subscription Progress
          </h3>
          <div className="flex items-center gap-4 mb-3">
            <Badge variant="info">{tenant.subscription_plan ?? 'None'}</Badge>
            <span className="text-sm text-neutral-500 dark:text-neutral-400 tabular-nums">
              {daysRemaining} days remaining
            </span>
          </div>
          <div className="w-full rounded-full h-2.5 bg-neutral-100 dark:bg-neutral-800">
            <div
              className={`${progressColor} h-2.5 rounded-full transition-all duration-500`}
              style={{ width: `${Math.min(progressPct, 100)}%` }}
            />
          </div>
          {tenant.subscription_started_at && (
            <p className="mt-2 text-xs text-neutral-400 dark:text-neutral-500 tabular-nums">
              {formatINRDate(tenant.subscription_started_at)} — {formatINRDate(tenant.subscription_expires_at)}
            </p>
          )}
        </div>
      )}

      {/* Details loading fallback */}
      {!details && <Skeleton variant="rect" height={180} className="rounded-2xl" />}

      {/* What the plan actually entitles this tenant to, and how much is used */}
      <TenantLimitsPanel tenantId={tenant.id} canManage={canManage} />

      {/* Plan actions */}
      <PlanActionsPanel
        tenantId={tenant.id}
        currentPlanName={tenant.subscription_plan}
        expiresAt={tenant.subscription_expires_at}
        tenantCurrency={tenant.default_currency}
        readOnly={!canManage}
      />

      {/* Invoices + history side-by-side on wide screens */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <InvoiceHistoryPanel tenantId={tenant.id} canDownload={canManage} />
        <ChangeHistoryTimeline tenantId={tenant.id} />
      </div>

      {/* Danger zone — SUPER_ADMIN only */}
      {canCancel && tenant.status !== 'CANCELLED' && tenant.status !== 'CHURNED' && (
        <div
          className={cn(
            'rounded-2xl border border-rose-200/70 dark:border-rose-900/40',
            'bg-gradient-to-br from-rose-50/90 via-white/80 to-white/80',
            'dark:from-rose-950/30 dark:via-neutral-900/80 dark:to-neutral-900/80',
            'backdrop-blur-sm p-6 shadow-[0_12px_30px_-18px_rgba(225,29,72,0.35)]',
          )}
        >
          <div className="flex flex-wrap items-start gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300">
              <TrashIcon />
            </div>
            <div className="flex-1 min-w-[220px]">
              <h3 className="text-base font-semibold text-rose-700 dark:text-rose-300">
                Danger zone
              </h3>
              <p className="mt-1 text-sm text-rose-700/80 dark:text-rose-200/80">
                Billing only: this voids pending invoices and records a CANCELLATION in the change
                history. The tenant keeps its current status and full access. To close the account
                and start the data lifecycle (Day 90 hard delete), use Cancel Tenant in the status
                menu at the top of this page.
              </p>
            </div>
            <Button
              variant="danger"
              onClick={() => setCancelOpen(true)}
            >
              Cancel subscription
            </Button>
          </div>
        </div>
      )}

      {/* ─── Dialogs ────────────────────────────────────────────────────── */}

      <Modal
        open={extendOpen}
        onClose={() => !extendMutation.isPending && setExtendOpen(false)}
        title="Extend trial"
        footer={
          <>
            <Button variant="secondary" onClick={() => setExtendOpen(false)} disabled={extendMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => extendMutation.mutate()}
              loading={extendMutation.isPending}
            >
              Extend by {extendDays} days
            </Button>
          </>
        }
      >
        <div className="space-y-4 text-sm text-neutral-700 dark:text-neutral-200">
          <p>
            Pick how many days to add. The trial end date moves forward; billing status stays TRIAL.
          </p>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              Duration
            </label>
            <RadioGroup
              variant="horizontal"
              value={extendDays}
              onChange={setExtendDays}
              options={EXTEND_OPTIONS}
            />
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              Internal note (optional)
            </label>
            <TextArea
              value={extendNotes}
              onChange={setExtendNotes}
              placeholder="e.g. Extended while awaiting signed contract"
              rows={2}
            />
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={reactivateOpen}
        title="Reactivate tenant"
        description="This restores full write access for 30 days and records a renewal event. A fresh invoice is generated at the current plan's monthly rate."
        confirmLabel="Reactivate for 30 days"
        variant="warning"
        loading={reactivateMutation.isPending}
        onConfirm={() => reactivateMutation.mutate()}
        onCancel={() => setReactivateOpen(false)}
      />

      <Modal
        open={cancelOpen}
        onClose={() => !cancelMutation.isPending && setCancelOpen(false)}
        title="Cancel subscription"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCancelOpen(false)} disabled={cancelMutation.isPending}>
              Keep subscription
            </Button>
            <Button
              variant="danger"
              onClick={() => cancelMutation.mutate()}
              loading={cancelMutation.isPending}
            >
              Confirm cancellation
            </Button>
          </>
        }
      >
        <div className="space-y-4 text-sm text-neutral-700 dark:text-neutral-200">
          <div className="rounded-xl bg-rose-50 dark:bg-rose-900/30 p-3 text-rose-800 dark:text-rose-200">
            <p className="text-sm">
              This records a CANCELLATION in the subscription history and voids every pending
              invoice. It does <strong>not</strong> change the tenant status, revoke access, or
              start the data lifecycle (Day 90 hard delete, Day 120 S3 cleanup) — Cancel Tenant in
              the status menu does that, and it needs Admin approval.
            </p>
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              Reason (recommended)
            </label>
            <TextArea
              value={cancelReason}
              onChange={setCancelReason}
              placeholder="e.g. Customer request — closing business"
              rows={3}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
